import { createStyles, Stack, Tabs } from '@mantine/core';
import {
	useApiGetAutomation,
	useApiUpdateAutomationDescription,
	useApiUpdateAutomationIcon,
	useApiUpdateAutomationTitle,
	useApiUpdateAutomationTriggerType,
} from '@repo/api-codegen';
import IconEmojiSelector from '@repo/common/components/IconEmojiSelector/IconEmojiSelector';
import { iconEmojiSelectorValueToString } from '@repo/common/components/IconEmojiSelector/utils';
import type { TabItem } from '@repo/common/components/TabsList';
import TabsList from '@repo/common/components/TabsList';
import { space } from '@repo/theme/primitives';
import { useDebounceFn } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router';
import type { Automation } from '../../../api';
import { useAuthUser } from '../../../api';
import { AddAutomationActionButtonV2 } from '../../../components/AutomationV2/AddAutomationActionButton';
import type { AutomationSectionOption } from '../../../components/AutomationV2/AutomationSection';
import { AutomationSection } from '../../../components/AutomationV2/AutomationSection';
import {
	AutomationSectionGroupNames,
	AutomationSectionType,
} from '../../../components/AutomationV2/constants';
import {
	getActionBlockTypeDescription,
	getActionBlockTypeIcon,
	getActionBlockTypeLabel,
} from '../../../components/AutomationV2/utils';
import { EntityPageDescription } from '../../../components/EntityPageLayout';
import EntityPageTitle from '../../../components/EntityPageLayout/EntityPageTitle';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutWrapper,
} from '../../../components/PageLayout';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { sanitizeMarkdownString } from '../../../utils/shared.utils';
import AutomationRunHistoryTab from '../AutomationRunHistoryTab';
import AutomationConfigurationSidebar from '../containers/AutomationConfigurationSidebar';
import AutomationPageNavBar from '../containers/AutomationPageNavbar';
import type { AutomationPageProps } from '../types';
import { ActionBlockType, TriggerBlockType } from './store';
import { useAutomationV2Store } from './useAutomationV2Store';

const useStyles = createStyles((theme) => ({
	stackContainer: {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
	},
	stack: {
		maxWidth: space[160], // 640px
		width: '100%',
		paddingTop: 0, // Override the default md padding
	},
	root: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		gap: theme.spacing['2xl'],
	},
	panel: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		alignItems: 'center',
	},
}));

const TABS_LIST: TabItem[] = [
	{ value: 'automation', label: 'Automation' },
	{ value: 'run_history', label: 'Run history' },
];

function AutomationPageV2({ id: propsId }: AutomationPageProps) {
	const { id: paramsId } = useParams();
	const id = propsId || paramsId;

	if (!id) {
		throw new Error('Unable to find automation ID');
	}

	const { classes } = useStyles();
	const [activeTab, setActiveTab] = useState<string | null>('automation');
	const { data: automation } = useApiGetAutomation({
		pathParams: {
			automationId: id,
		},
	});
	const store = useAutomationV2Store();
	const { workspace } = useAuthUser();
	const automationTitle = useMemo(
		() => ({
			title: store.automation?.title ?? '',
			title_cased: store.automation?.title ?? '',
		}),
		[store.automation]
	);
	const { automationAiGeneration } = useFeatureFlags();

	useEffect(() => {
		if (automation) {
			store.setAutomation(automation);
		}
	}, [automation, store]);

	const { mutateAsync: updateAutomationTitle } = useApiUpdateAutomationTitle();
	const { mutateAsync: updateAutomationDescription } =
		useApiUpdateAutomationDescription();
	const { mutateAsync: updateAutomationIcon } = useApiUpdateAutomationIcon();
	const { mutateAsync: updateTriggerType } =
		useApiUpdateAutomationTriggerType();

	const { run: handleUpdateTitle } = useDebounceFn(
		async (value) => {
			if (!store.automation?.id) {
				return;
			}

			if (value) {
				await updateAutomationTitle({
					pathParams: { automationId: store.automation?.id },
					body: {
						title: value,
					},
				});
			}
		},
		{ wait: 450 }
	);

	// No need to debounce description updates because it is handled by the
	// EntityPageDescription component
	const handleUpdateDescription = useCallback(
		async (value: string) => {
			if (!store.automation?.id) {
				return;
			}

			let updatedValue = value;

			// Need to handle the specific markdown string from RichEditor
			if (value.charCodeAt(0) === 92) {
				updatedValue = sanitizeMarkdownString(value);
			}

			await updateAutomationDescription({
				pathParams: { automationId: store.automation.id },
				body: {
					description: updatedValue || '',
				},
			});
		},
		[updateAutomationDescription, store]
	);

	const handleIconChange = useCallback(
		async (value: string) => {
			if (store.automation?.id) {
				await updateAutomationIcon({
					pathParams: { automationId: store.automation.id },
					body: {
						icon: value,
					},
				});
			}
		},
		[updateAutomationIcon, store]
	);

	const handleTriggerTypeChange = useCallback(
		(triggerType: TriggerBlockType) => {
			if (store.automation) {
				updateTriggerType({
					pathParams: { automationId: store.automation?.id },
					body: { trigger_type: triggerType },
				});
			}
		},
		[updateTriggerType, store]
	);

	const automationActions = useMemo(() => {
		const actions: (AutomationSectionOption | null)[] = Object.values(
			ActionBlockType
		).map((action) => {
			if (
				action === ActionBlockType.GENERATE_AI_OUTPUT &&
				!automationAiGeneration
			) {
				return null;
			}

			return {
				iconName: getActionBlockTypeIcon(action),
				title: getActionBlockTypeLabel(action),
				description: getActionBlockTypeDescription(action),
				onClick: () => store.addActionBlockType(action),
				isBeta: action === ActionBlockType.GENERATE_AI_OUTPUT,
				groupName: [
					ActionBlockType.SEND_ANNOUNCEMENT,
					ActionBlockType.SEND_EMAIL,
					ActionBlockType.SEND_SLACK_MESSAGE,
				].includes(action)
					? AutomationSectionGroupNames.NOTIFICATION
					: AutomationSectionGroupNames.ACTION,
				aiIcon: action === ActionBlockType.GENERATE_AI_OUTPUT,
			};
		});

		return actions.filter((action) => action !== null);
	}, [store, automationAiGeneration]);

	if (!workspace.automation_enabled) {
		return <Navigate to="/" />;
	}

	const icon = (
		<IconEmojiSelector
			value={
				store.automation?.icon ??
				iconEmojiSelectorValueToString({
					iconName: 'bolt',
					iconColor: '#4a4a4a',
				})
			}
			onChange={handleIconChange}
			entityType={'automation'}
		/>
	);

	return (
		<PageLayoutWrapper name="automationV2">
			<PageLayoutContentWrapper name="automationV2">
				{automation && (
					<AutomationPageNavBar
						automation={automation as Automation}
						navigateToHistory={() => {
							setActiveTab('run_history');
						}}
					/>
				)}

				<PageLayoutContent className={classes.stackContainer} mb="0">
					<Stack spacing="xs" className={classes.stack} pb="5xl">
						{automation && store.automation && (
							<>
								<EntityPageTitle
									entity={automationTitle}
									onChange={handleUpdateTitle}
									placeholder="Untitled"
									isReadOnly={false}
									icon={icon}
									wrapTitle
								/>
								<EntityPageDescription
									entityId={store.automation.id}
									onChange={handleUpdateDescription}
									description={store.automation.description ?? ''}
								/>
								<Tabs
									color="dark"
									classNames={{
										root: classes.root,
										panel: classes.panel,
									}}
									defaultValue="automation"
									value={activeTab}
									onTabChange={setActiveTab}
									keepMounted={false}
								>
									<TabsList tabs={TABS_LIST} />
									<Tabs.Panel value="automation" pt="sm">
										<AutomationSection
											title="Run automation when..."
											options={[
												{
													iconName: 'clock',
													title: 'Scheduled',
													onClick: () =>
														handleTriggerTypeChange(TriggerBlockType.SCHEDULE),
													groupName: AutomationSectionGroupNames.TRIGGER,
												},
												{
													iconName: 'schema',
													title: 'Schemas are changed',
													onClick: () =>
														handleTriggerTypeChange(TriggerBlockType.SCHEMA),
													groupName: AutomationSectionGroupNames.TRIGGER,
												},
											]}
											sectionType={AutomationSectionType.TRIGGER}
										/>
										<AutomationSection
											title="Then, do these things..."
											options={automationActions}
											sectionType={AutomationSectionType.ACTION}
											canAddMoreOptions={!store.isBlockExhausted}
										/>
										{!store.isBlockExhausted && !store.isBlockFull && (
											<AddAutomationActionButtonV2 />
										)}
									</Tabs.Panel>
									<Tabs.Panel value="run_history" pt="sm">
										<AutomationRunHistoryTab
											automationId={store.automation.id}
										/>
									</Tabs.Panel>
								</Tabs>
							</>
						)}
					</Stack>
				</PageLayoutContent>
			</PageLayoutContentWrapper>
			{automation && store.automation && (
				<AutomationConfigurationSidebar automation={store.automation} />
			)}
		</PageLayoutWrapper>
	);
}

const AutomationPageV2Component = observer(AutomationPageV2);
export default AutomationPageV2Component;
